/**
 * Development-only test mode for browser automation testing
 * Allows bypassing auth to test authenticated views
 *
 * Usage:
 * - Add ?testMode=comedy_central_fan (or any test username) to URL
 * - Only works when VITE_DEV_TEST_MODE=true and in development
 *
 * Test users from seed.sql:
 * - comedy_central_fan, movie_maven_sam, cinephile_alex, classic_cinema_buff
 * - drama_queen_lily, horror_fanatic_zack, anime_addict_yuki, scifi_sage_maya
 * - rom_com_lover_riley, action_hero_jake
 */

import { ref } from 'vue'

const isTestMode = ref(false)
const testUser = ref(null)
const testProfile = ref(null)

// Test user data from seed.sql (with mock profile data)
const TEST_USER_MAP = {
  comedy_central_fan: {
    id: '00000000-0000-1000-0000-000000000001',
    email: 'test1@example.com',
    username: 'comedy_central_fan',
    display_name: 'Comedy Central Fan'
  },
  movie_maven_sam: {
    id: '00000000-0000-1000-0000-000000000002',
    email: 'test2@example.com',
    username: 'movie_maven_sam',
    display_name: 'Movie Maven Sam'
  },
  cinephile_alex: {
    id: '00000000-0000-1000-0000-000000000003',
    email: 'test3@example.com',
    username: 'cinephile_alex',
    display_name: 'Cinephile Alex'
  },
  classic_cinema_buff: {
    id: '00000000-0000-1000-0000-000000000004',
    email: 'test4@example.com',
    username: 'classic_cinema_buff',
    display_name: 'Classic Cinema Buff'
  },
  drama_queen_lily: {
    id: '00000000-0000-1000-0000-000000000005',
    email: 'test5@example.com',
    username: 'drama_queen_lily',
    display_name: 'Drama Queen Lily'
  },
  horror_fanatic_zack: {
    id: '00000000-0000-1000-0000-000000000006',
    email: 'test6@example.com',
    username: 'horror_fanatic_zack',
    display_name: 'Horror Fanatic Zack'
  },
  anime_addict_yuki: {
    id: '00000000-0000-1000-0000-000000000007',
    email: 'test7@example.com',
    username: 'anime_addict_yuki',
    display_name: 'Anime Addict Yuki'
  },
  scifi_sage_maya: {
    id: '00000000-0000-1000-0000-000000000008',
    email: 'test8@example.com',
    username: 'scifi_sage_maya',
    display_name: 'Sci-Fi Sage Maya'
  },
  rom_com_lover_riley: {
    id: '00000000-0000-1000-0000-000000000009',
    email: 'test9@example.com',
    username: 'rom_com_lover_riley',
    display_name: 'Rom-Com Lover Riley'
  },
  action_hero_jake: {
    id: '00000000-0000-1000-0000-000000000010',
    email: 'test10@example.com',
    username: 'action_hero_jake',
    display_name: 'Action Hero Jake'
  }
}

export function useTestMode() {
  const isDevMode = import.meta.env.DEV
  const testModeEnabled = import.meta.env.VITE_DEV_TEST_MODE === 'true'

  /**
   * Check URL for test mode parameter and initialize if valid
   * Call this early in app initialization
   */
  async function initTestMode() {
    if (!isDevMode || !testModeEnabled) {
      console.log(
        '[TestMode] Disabled - not in dev mode or VITE_DEV_TEST_MODE not set'
      )
      return false
    }

    const urlParams = new URLSearchParams(window.location.search)
    const testUsername = urlParams.get('testMode')

    if (!testUsername) {
      return false
    }

    const testUserData = TEST_USER_MAP[testUsername]
    if (!testUserData) {
      console.warn(`[TestMode] Unknown test user: ${testUsername}`)
      console.log(
        '[TestMode] Available users:',
        Object.keys(TEST_USER_MAP).join(', ')
      )
      return false
    }

    console.log(
      `[TestMode] Activating for user: ${testUsername} (${testUserData.id})`
    )

    // Create mock user object matching Supabase auth user structure
    testUser.value = {
      id: testUserData.id,
      email: testUserData.email,
      user_metadata: { name: testUserData.display_name },
      app_metadata: {},
      aud: 'authenticated',
      role: 'authenticated',
      created_at: new Date().toISOString()
    }

    // Create mock profile matching the profiles table structure
    testProfile.value = {
      id: testUserData.id,
      user_id: testUserData.id,
      username: testUserData.username,
      display_name: testUserData.display_name,
      avatar_url: null,
      is_admin: false,
      has_access: true,
      needs_onboarding: false,
      needs_welcome: false,
      is_onboarded: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }

    isTestMode.value = true

    console.log('[TestMode] Successfully activated:', {
      username: testProfile.value.username,
      displayName: testProfile.value.display_name
    })

    return true
  }

  /**
   * Inject test user into auth store
   * Call after initTestMode() returns true
   */
  function injectIntoAuthStore(storeAuth) {
    if (!isTestMode.value || !testUser.value) {
      return false
    }

    // Create mock session
    const mockSession = {
      access_token: 'test-mode-token',
      refresh_token: 'test-mode-refresh',
      expires_in: 3600,
      expires_at: Math.floor(Date.now() / 1000) + 3600,
      token_type: 'bearer',
      user: testUser.value
    }

    // Inject into store
    storeAuth.user = testUser.value
    storeAuth.session = mockSession
    storeAuth.userLoaded = true
    storeAuth.userNotAuthed = false
    storeAuth.inited = true

    console.log('[TestMode] Injected into auth store')
    return true
  }

  /**
   * Inject test profile into profile store
   */
  function injectIntoProfileStore(storeProfile) {
    if (!isTestMode.value || !testProfile.value) {
      return false
    }

    // Add the test user to the users array
    storeProfile.users = [testProfile.value]
    storeProfile.currentUserId = testProfile.value.id

    // Set all the loaded/access flags
    storeProfile.loaded = true
    storeProfile.loading = false
    storeProfile.currentUserLoaded = true
    storeProfile.currentUserHasAccess = true
    storeProfile.currentUserIsAdmin = false
    storeProfile.currentUserNeedsOnboarding = false
    storeProfile.currentUserPendingDeletion = false
    storeProfile.currentSessionSeenWelcome = true
    storeProfile.currentSessionSeenOnboarding = true

    // Initialize empty follows/following for test mode
    storeProfile.follows = []
    storeProfile.following = []

    console.log('[TestMode] Injected into profile store')
    return true
  }

  /**
   * Get mobile iOS simulation config
   * Use with ?testMode=username&platform=ios
   */
  function getMobileConfig() {
    const urlParams = new URLSearchParams(window.location.search)
    const platform = urlParams.get('platform')

    if (platform === 'ios') {
      return {
        platform: 'ios',
        isNative: true,
        userAgent:
          'Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148'
      }
    }

    return null
  }

  /**
   * Apply mobile simulation (Ionic mode, Capacitor mock)
   */
  function applyMobileSimulation() {
    const config = getMobileConfig()
    if (!config) return false

    // Set Ionic to iOS mode
    document.documentElement.setAttribute('mode', 'ios')
    document.body.classList.remove('md')
    document.body.classList.add('ios', 'platform-ios')

    // Mock Capacitor if it exists
    if (window.Capacitor) {
      window.Capacitor.getPlatform = () => config.platform
      window.Capacitor.isNativePlatform = () => config.isNative
    }

    console.log('[TestMode] Applied iOS mobile simulation')
    return true
  }

  return {
    isTestMode,
    testUser,
    testProfile,
    initTestMode,
    injectIntoAuthStore,
    injectIntoProfileStore,
    getMobileConfig,
    applyMobileSimulation,
    TEST_USER_MAP
  }
}
